import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Target, Filter, Save, RefreshCw, ChevronDown, ChevronUp, User, X } from 'lucide-react';
import toast from 'react-hot-toast';

export default function MyDealsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLead, setExpandedLead] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [savingLead, setSavingLead] = useState(null);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { source: 'public_lead', limit: 200 };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await axios.get('/api/submissions', { params });
      setLeads(res.data.data || []);
    } catch {
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { loadLeads(); }, []);

  // Detect status / category staff select fields from loaded leads
  const { statusField, categoryField, statusOptions, categoryOptions } = useMemo(() => {
    const fieldMap = {};
    for (const lead of leads) {
      const schema = typeof lead.form_schema === 'string'
        ? JSON.parse(lead.form_schema) : (lead.form_schema || {});
      for (const f of (schema.fields || [])) {
        if (f.visibility === 'staff' && f.type === 'select') fieldMap[f.id] = f;
      }
    }
    const selectFields = Object.values(fieldMap);
    const statusF = selectFields.find(f =>
      f.label.includes('狀態') || f.label.includes('進度') || f.label.toLowerCase().includes('status')
    ) || selectFields[0] || null;
    const categoryF = selectFields.find(f =>
      f.label.includes('類別') || f.label.includes('分類') || f.label.toLowerCase().includes('category')
    ) || (selectFields[1] && selectFields[1].id !== statusF?.id ? selectFields[1] : null);

    const getUnique = (fieldId) => {
      const vals = new Set();
      leads.forEach(l => { const v = (l.data || {})[fieldId]; if (v) vals.add(v); });
      return [...vals];
    };

    return {
      statusField: statusF,
      categoryField: categoryF,
      statusOptions: statusF ? getUnique(statusF.id) : [],
      categoryOptions: categoryF ? getUnique(categoryF.id) : [],
    };
  }, [leads]);

  const filteredLeads = useMemo(() => leads.filter(lead => {
    const data = lead.data || {};
    if (statusFilter && statusField && data[statusField.id] !== statusFilter) return false;
    if (categoryFilter && categoryField && data[categoryField.id] !== categoryFilter) return false;
    return true;
  }), [leads, statusFilter, categoryFilter, statusField, categoryField]);

  const getSchema = (lead) => {
    const schema = typeof lead.form_schema === 'string'
      ? JSON.parse(lead.form_schema) : (lead.form_schema || {});
    const all = schema.fields || [];
    return {
      customerFields: all.filter(f => !f.visibility || f.visibility === 'customer' || f.visibility === 'both'),
      staffFields: all.filter(f => f.visibility === 'staff'),
    };
  };

  const getDisplayName = (lead, customerFields) => {
    const data = lead.data || {};
    for (const f of customerFields) {
      if (f.label.includes('姓') || f.label.includes('名') || f.label.toLowerCase().includes('name')) {
        if (data[f.id]) return data[f.id];
      }
    }
    return Object.values(data)[0] || lead.form_title || '—';
  };

  const updateStaffField = (leadId, fieldId, value) => {
    setEditingData(prev => ({ ...prev, [leadId]: { ...(prev[leadId] || {}), [fieldId]: value } }));
  };

  const handleSave = async (lead) => {
    const staffData = editingData[lead.id];
    if (!staffData) return;
    setSavingLead(lead.id);
    try {
      await axios.put(`/api/submissions/${lead.id}`, { data: staffData });
      toast.success('已儲存');
      loadLeads();
      setEditingData(prev => { const n = { ...prev }; delete n[lead.id]; return n; });
    } catch { toast.error('儲存失敗'); }
    finally { setSavingLead(null); }
  };

  const hasFilters = fromDate || toDate || statusFilter || categoryFilter;

  return (
    <div style={{ padding: 32 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>我的商機</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 14, marginTop: 4 }}>
            顯示 {filteredLeads.length} 筆 / 共 {leads.length} 筆
          </p>
        </div>
        <button onClick={loadLeads} className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <RefreshCw size={14} /> 重新整理
        </button>
      </div>

      {/* Filter Bar */}
      <div className="card" style={{ padding: '14px 16px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>建檔日期 從</label>
          <input type="date" className="input" value={fromDate}
            onChange={e => setFromDate(e.target.value)} style={{ fontSize: 13, width: 148 }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>至</label>
          <input type="date" className="input" value={toDate}
            onChange={e => setToDate(e.target.value)} style={{ fontSize: 13, width: 148 }} />
        </div>
        {statusField && statusOptions.length > 0 && (
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>
              {statusField.label}
            </label>
            <select className="input" value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)} style={{ fontSize: 13, width: 130 }}>
              <option value="">全部</option>
              {statusOptions.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        )}
        {categoryField && categoryOptions.length > 0 && (
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>
              {categoryField.label}
            </label>
            <select className="input" value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)} style={{ fontSize: 13, width: 130 }}>
              <option value="">全部</option>
              {categoryOptions.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        )}
        <button onClick={loadLeads} className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Filter size={14} /> 套用日期
        </button>
        {hasFilters && (
          <button onClick={() => { setFromDate(''); setToDate(''); setStatusFilter(''); setCategoryFilter(''); }}
            className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <X size={14} /> 清除
          </button>
        )}
      </div>

      {/* Lead List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>載入中...</div>
      ) : filteredLeads.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <Target size={40} color="var(--text-3)" style={{ marginBottom: 12 }} />
          <p style={{ color: 'var(--text-2)', fontSize: 15 }}>
            {hasFilters ? '沒有符合篩選條件的商機' : '尚無商機資料'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {filteredLeads.map(lead => {
            const isExpanded = expandedLead === lead.id;
            const { customerFields, staffFields } = getSchema(lead);
            const data = lead.data || {};
            const staffEdit = editingData[lead.id] || {};
            const displayName = getDisplayName(lead, customerFields);

            return (
              <div key={lead.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                  onClick={() => setExpandedLead(isExpanded ? null : lead.id)}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <User size={16} color="#059669" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {displayName}
                      <span className="badge badge-green" style={{ marginLeft: 8, fontSize: 10 }}>{lead.form_title}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-2)', marginTop: 2, flexWrap: 'wrap' }}>
                      {customerFields.slice(0, 3).map(f => {
                        const v = data[f.id];
                        return v ? <span key={f.id}>{f.label}: {v}</span> : null;
                      }).filter(Boolean)}
                      {staffFields.slice(0, 2).map(f => {
                        const v = data[f.id];
                        return v ? <span key={f.id} style={{ color: 'var(--primary)' }}>• {f.label}: {v}</span> : null;
                      }).filter(Boolean)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                      {new Date(lead.submitted_at).toLocaleDateString('zh-TW')}
                    </span>
                    {isExpanded ? <ChevronUp size={16} color="var(--text-3)" /> : <ChevronDown size={16} color="var(--text-3)" />}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ padding: '0 18px 16px', borderTop: '1px solid var(--border)' }}>
                    {customerFields.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: 8 }}>客戶填寫</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                          {customerFields.map(f => (
                            <div key={f.id} style={{ fontSize: 13, padding: '4px 0' }}>
                              <span style={{ color: 'var(--text-3)' }}>{f.label}：</span>
                              <span style={{ fontWeight: 500 }}>{data[f.id] || '—'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {staffFields.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', marginBottom: 8 }}>業務填寫</div>
                        <div style={{ display: 'grid', gap: 8 }}>
                          {staffFields.map(f => {
                            const currentVal = staffEdit[f.id] ?? data[f.id] ?? '';
                            return (
                              <div key={f.id}>
                                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4, display: 'block' }}>
                                  {f.label}
                                </label>
                                {f.type === 'select' ? (
                                  <select className="input" value={currentVal}
                                    onChange={e => updateStaffField(lead.id, f.id, e.target.value)}
                                    style={{ fontSize: 13 }}>
                                    <option value="">請選擇</option>
                                    {(f.options || []).map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                                  </select>
                                ) : f.type === 'checkbox' ? (
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {(f.options || []).map((opt, i) => (
                                      <label key={i} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={(currentVal || '').split(',').includes(opt)}
                                          onChange={e => {
                                            const arr = (currentVal || '').split(',').filter(Boolean);
                                            if (e.target.checked) arr.push(opt); else arr.splice(arr.indexOf(opt), 1);
                                            updateStaffField(lead.id, f.id, arr.join(','));
                                          }} />
                                        {opt}
                                      </label>
                                    ))}
                                  </div>
                                ) : f.type === 'date' ? (
                                  <input type="date" className="input" value={currentVal}
                                    onChange={e => updateStaffField(lead.id, f.id, e.target.value)}
                                    style={{ fontSize: 13 }} />
                                ) : (
                                  <input className="input" value={currentVal}
                                    onChange={e => updateStaffField(lead.id, f.id, e.target.value)}
                                    placeholder={f.placeholder || ''} style={{ fontSize: 13 }} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <button onClick={() => handleSave(lead)}
                          disabled={savingLead === lead.id || !editingData[lead.id]}
                          className="btn btn-primary btn-sm" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Save size={13} /> {savingLead === lead.id ? '儲存中...' : '儲存業務欄位'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
